import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { AppList, AppFlexContainer, AppFlexItem } from 'client/ui-building-blocks'
import { IQuestion } from 'cat-shared/utils'
import { QuestionModal } from 'client/components'
import { useModalDialogContext, usePage, usePageStateContext } from 'client/hooks'
import { type TGetQuestionsData } from 'client/api/useGetQuestions/useGetQuestions'

import QuestionListItem from './question-list-item'

type Props = {
  questionsData: TGetQuestionsData
  isLoading: boolean
  isFetching: boolean
  title?: string
  pageSize: number
  activePage?: number
}

const QuestionsList = ({
  questionsData = { count: 0, data: [] },
  isLoading,
  isFetching,
  pageSize,
  title,
  activePage,
}: Props) => {
  const { t } = useTranslation()
  const { pathname } = useLocation()

  const { navigateTo } = usePage()

  const { listActiveQuestionId, setBrowseQuestionsPageState } = usePageStateContext()

  const { showQuestionModal, toggleQuestionModal, closeAllModals } =
    useModalDialogContext()

  useEffect(() => {
    if (listActiveQuestionId) {
      toggleQuestionModal(true)
    } else {
      closeAllModals()
    }
  }, [listActiveQuestionId])

  const onPageChange = (pageNo: number) => {
    if (!isLoading && !isFetching) {
      setBrowseQuestionsPageState(state => ({ ...state, pageNo }))
    }
  }

  const renderItem = (item: IQuestion) => {
    const onItemClick = () => {
      if (!isLoading && !isFetching) {
        navigateTo({
          path: pathname,
          query: { questionId: item._id.toString() },
          preventScroll: true,
        })
      }
    }

    return (
      <AppFlexItem
        key={item._id.toString()}
        flexGrow={1}
        flexBasis="calc(100% / 3 - 15px)"
        minWidth="275px"
        maxWidth="550px"
      >
        <QuestionListItem
          key={item._id.toString()}
          activePage={activePage}
          item={item}
          isFetching={isLoading || isFetching}
          onItemClick={onItemClick}
        />
      </AppFlexItem>
    )
  }

  return (
    <>
      <AppList
        id="questions-list"
        data={questionsData}
        title={title}
        isLoading={isLoading}
        isFetching={isFetching}
        noItemsMsg={t('common.info.nothingToDisplay')}
        pageSize={pageSize}
        activePage={activePage}
        onPageChange={onPageChange}
      >
        <AppFlexContainer justifyContent="center" rowGap="15px" columnGap="15px">
          {questionsData.data?.map(renderItem)}
        </AppFlexContainer>
      </AppList>
      <QuestionModal
        questionsData={questionsData}
        question={questionsData.data.find(q => q._id.toString() === listActiveQuestionId)}
        open={showQuestionModal}
      />
    </>
  )
}

export default QuestionsList
